import { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { X } from 'phosphor-react';
import { theme } from '../../styles/theme';

interface GoalSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (goal: number) => Promise<void>;
  initialGoal?: number;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: ${theme.zIndex.modal};
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: flex-end;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease-out;
`;

const Modal = styled.div`
  background: var(--color-bg);
  border-radius: 24px 24px 0 0;
  width: 100%;
  max-width: 375px;
  padding: 24px;
  padding-bottom: 40px;
  animation: ${slideUp} 0.3s ease-out;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-border-subtle);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text);
  cursor: pointer;
  transition: background ${theme.transitions.fast};

  &:hover {
    background: var(--color-border);
  }
`;

const Description = styled.p`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 24px;
  line-height: 1.5;
`;

const GoalDisplay = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const GoalValue = styled.div`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 48px;
  font-weight: 700;
  color: var(--color-primary);
`;

const GoalUnit = styled.div`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-top: 4px;
`;

const SliderContainer = styled.div`
  margin-bottom: 24px;
`;

const SliderTrack = styled.input`
  width: 100%;
  height: 8px;
  -webkit-appearance: none;
  appearance: none;
  background: var(--color-border-subtle);
  border-radius: 4px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--color-primary);
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(131, 76, 201, 0.4);
  }

  &::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--color-primary);
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 8px rgba(131, 76, 201, 0.4);
  }
`;

const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 12px;
  color: var(--color-text-muted);
`;

const PresetButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

const PresetButton = styled.button<{ $selected: boolean }>`
  flex: 1;
  min-width: 70px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'var(--color-border)')};
  background: ${({ $selected }) => ($selected ? 'rgba(131, 76, 201, 0.2)' : 'transparent')};
  color: ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'var(--color-text)')};
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
`;

const SaveButton = styled.button<{ $disabled: boolean }>`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: none;
  background: ${({ $disabled }) => ($disabled ? 'rgba(131, 76, 201, 0.3)' : 'var(--color-primary)')};
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 16px;
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all ${theme.transitions.fast};

  &:hover:not(:disabled) {
    background: var(--color-primary-dark);
  }
`;

const ErrorText = styled.p`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 13px;
  color: #FF6B6B;
  text-align: center;
  margin: 12px 0 0;
`;

const PRESETS = [1500, 2000, 2500, 3000];

export const GoalSetupModal: React.FC<GoalSetupModalProps> = ({ isOpen, onClose, onSave, initialGoal }) => {
  const [goal, setGoal] = useState(initialGoal || 2000);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen && initialGoal) {
      setGoal(initialGoal);
    }
    if (isOpen) {
      setSaveError(null);
    }
  }, [isOpen, initialGoal]);

  // Close on escape key (listener only attached while open)
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Move focus into the dialog on open, restore it on close/unmount
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    modalRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Round to nearest 50
    const value = Math.round(parseInt(e.target.value) / 50) * 50;
    setGoal(value);
  };

  const handlePresetClick = (preset: number) => {
    setGoal(preset);
  };

  const handleSave = async () => {
    if (isSaving) return;

    try {
      setIsSaving(true);
      setSaveError(null);
      await onSave(goal);
      onClose();
    } catch (error) {
      console.error('Error saving goal:', error);
      setSaveError('Failed to save goal. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <Modal
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="goal-setup-title"
        tabIndex={-1}
      >
        <Header>
          <Title id="goal-setup-title">Set Your Daily Goal</Title>
          <CloseButton onClick={onClose} aria-label="Close">
            <X size={20} weight="bold" />
          </CloseButton>
        </Header>

        <Description>
          Choose your daily calorie target. You can always change this later in settings.
        </Description>

        <GoalDisplay>
          <GoalValue>{goal.toLocaleString()}</GoalValue>
          <GoalUnit>calories per day</GoalUnit>
        </GoalDisplay>

        <SliderContainer>
          <SliderTrack
            type="range"
            min="1000"
            max="4000"
            step="50"
            value={goal}
            onChange={handleSliderChange}
            aria-label="Daily calorie goal"
          />
          <SliderLabels>
            <span>1,000</span>
            <span>4,000</span>
          </SliderLabels>
        </SliderContainer>

        <PresetButtons>
          {PRESETS.map((preset) => (
            <PresetButton
              key={preset}
              $selected={goal === preset}
              onClick={() => handlePresetClick(preset)}
            >
              {preset.toLocaleString()}
            </PresetButton>
          ))}
        </PresetButtons>

        <SaveButton onClick={handleSave} $disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Set Goal'}
        </SaveButton>

        {saveError && <ErrorText role="alert">{saveError}</ErrorText>}
      </Modal>
    </Overlay>
  );
};
